import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, MessagesSquare, Send, Timer, Archive, Star, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell } from "recharts";
import { PageHeader } from "@/components/layout/PageHeader";
import { api, errMsg } from "@/lib/api";
import { duration, initials } from "@/lib/format";

const PERIODS = [{ d: 7, l: "7 giorni" }, { d: 30, l: "30 giorni" }];

const download = async (format, days, setBusy) => {
  setBusy(format);
  try {
    const res = await api.get("/reports/agents/export", { params: { format, days }, responseType: "blob" });
    const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `report-agenti.${format}`;
    const url = URL.createObjectURL(res.data);
    const a = Object.assign(document.createElement("a"), { href: url, download: name });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`Report ${format.toUpperCase()} scaricato`);
  } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
};

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600">
    <Star size={12} fill="currentColor" />{value === null ? "—" : value.toFixed(2)}
  </span>
);

const Total = ({ label, value, icon: Icon, testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 fade-up">
    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}<Icon size={16} className="text-slate-400" /></div>
    <div className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">{value}</div>
  </div>
);

export default function Report() {
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(null);
  const { data } = useQuery({ queryKey: ["report", days], queryFn: () => api.get("/reports/agents", { params: { days } }).then((r) => r.data), refetchInterval: 15000 });
  const rows = data?.rows || [];
  const sum = (k) => rows.reduce((a, r) => a + r[k], 0);
  const resp = rows.filter((r) => r.avg_response_seconds !== null);
  const avg = resp.length ? Math.round(resp.reduce((a, r) => a + r.avg_response_seconds * r.responses, 0) / Math.max(1, resp.reduce((a, r) => a + r.responses, 0))) : 0;
  const chart = rows.filter((r) => r.chats || r.closed).map((r) => ({ name: r.name.split(" ")[0], gestite: r.chats, chiuse: r.closed, color: r.color }));

  return (
    <div className="h-full flex flex-col">
      <PageHeader title="Report agenti" subtitle="Prestazioni del team nel periodo selezionato" icon={BarChart3}>
        <div className="flex bg-slate-100 rounded-lg p-1">
          {PERIODS.map((p) => (
            <button key={p.d} data-testid={`report-period-${p.d}`} onClick={() => setDays(p.d)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${days === p.d ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{p.l}</button>
          ))}
        </div>
        <button data-testid="report-export-xlsx" disabled={!!busy} onClick={() => download("xlsx", days, setBusy)}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
          <FileSpreadsheet size={15} className="text-[#15803D]" />{busy === "xlsx" ? "Generazione…" : "Excel"}
        </button>
        <button data-testid="report-export-pdf" disabled={!!busy} onClick={() => download("pdf", days, setBusy)}
          className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#0F172A] text-xs font-semibold text-white hover:bg-[#1E293B] transition-colors disabled:opacity-50">
          <FileText size={15} />{busy === "pdf" ? "Generazione…" : "PDF"}
        </button>
      </PageHeader>
      <div className="flex-1 overflow-y-auto thin-scroll p-4 md:p-8 space-y-6">
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-3 md:gap-5">
          <Total testid="report-total-chats" label="Chat gestite" value={sum("chats")} icon={MessagesSquare} />
          <Total testid="report-total-sent" label="Messaggi inviati" value={sum("sent")} icon={Send} />
          <Total testid="report-total-avg" label="Tempo medio risposta" value={duration(avg)} icon={Timer} />
          <Total testid="report-total-closed" label="Chat chiuse" value={sum("closed")} icon={Archive} />
          <Total testid="report-total-csat" label={`Soddisfazione (${data?.csat?.count ?? 0})`} value={data?.csat?.avg != null ? `${data.csat.avg.toFixed(1)} / 5` : "—"} icon={Star} />
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-5 items-start">
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="report-table">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-5 py-3 font-semibold">Agente</th><th className="text-right px-4 font-semibold">Chat gestite</th><th className="text-right px-4 font-semibold">Inviati</th><th className="text-right px-4 font-semibold">T. medio risposta</th><th className="text-right px-4 font-semibold">Chiuse</th><th className="text-right px-5 font-semibold">Soddisfazione</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`report-row-${r.email}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full grid place-items-center text-[11px] font-bold text-white" style={{ background: r.color }}>{initials(r.name)}</span>
                        <div><div className="font-semibold text-slate-800">{r.name}</div><div className="text-xs text-slate-400">{r.role === "admin" ? "Amministratore" : "Agente"}</div></div>
                      </div>
                    </td>
                    <td className="text-right px-4 font-semibold text-slate-900" data-testid="report-cell-chats">{r.chats}</td>
                    <td className="text-right px-4 text-slate-600">{r.sent}</td>
                    <td className="text-right px-4 text-slate-600">{r.avg_response_seconds === null ? "—" : duration(r.avg_response_seconds)}</td>
                    <td className="text-right px-4"><span className="inline-block min-w-7 text-center text-xs font-bold px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#15803D]">{r.closed}</span></td>
                    <td className="text-right px-5" data-testid="report-cell-csat"><Stars value={r.csat_avg} /><span className="text-[10px] text-slate-400 ml-1">({r.csat_count})</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="report-chart" >
            <h3 className="text-base font-semibold text-slate-900">Chat gestite per agente</h3>
            <p className="text-xs text-slate-500 mb-3">Ultimi {days} giorni</p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 4, left: -22, bottom: 0 }}>
                  <CartesianGrid stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} cursor={{ fill: "#F8FAFC" }} />
                  <Bar dataKey="gestite" radius={[6, 6, 0, 0]}>{chart.map((c) => <Cell key={c.name} fill={c.color} />)}</Bar>
                  <Bar dataKey="chiuse" fill="#25D366" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
